import React, { useEffect, useState } from 'react';

const inputClass = 'input-dark w-full py-3 min-h-[44px]';

export default function ConvertirAvoirModal({
  open,
  vente,
  api,
  onClose,
  onCreated,
}) {
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [error, setError] = useState('');
  const [conversion, setConversion] = useState('totale');
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [clientNom, setClientNom] = useState('');
  const [clientTel, setClientTel] = useState('');
  const [clientId, setClientId] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const sourceRef = vente?.ref_vente || '';
  const sourceType = vente?.type === 'acompte' ? 'acompte' : 'vente';
  const clientLocked = Boolean(eligible?.client_locked);

  useEffect(() => {
    if (!open || !sourceRef) return undefined;
    let cancelled = false;
    setError('');
    setEligible(null);
    setConversion('totale');
    setMotif('');
    setSuggestions([]);
    (async () => {
      try {
        const res = await api.get('/avoirs/eligible', {
          params: { source_type: sourceType, source_ref: sourceRef },
        });
        if (cancelled) return;
        setEligible(res.data);
        setMontant(String(res.data.montant_eligible ?? ''));
        setClientNom(res.data.client_nom || vente?.client_nom || '');
        setClientTel(res.data.client_tel || vente?.client_tel || '');
        setClientId(res.data.client_id || '');
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Impossible de calculer le montant éligible');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, sourceRef, sourceType, api, vente]);

  useEffect(() => {
    if (!open || clientLocked) {
      setSuggestions([]);
      return undefined;
    }
    const q = (clientNom || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/clients/autocomplete/${encodeURIComponent(q)}`);
        if (!cancelled) setSuggestions(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, clientLocked, clientNom, api]);

  if (!open) return null;

  const max = Number(eligible?.montant_eligible || 0);
  const submit = async () => {
    const amount = conversion === 'totale' ? max : (parseFloat(montant) || 0);
    if (amount <= 0) {
      setError('Montant invalide');
      return;
    }
    if (amount > max + 0.001) {
      setError(`Le montant ne peut pas dépasser ${max.toFixed(2)} €`);
      return;
    }
    if (!clientLocked && !(clientNom || '').trim()) {
      setError('Indiquez le nom du client pour cet avoir');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/avoirs/from-document', {
        source_type: sourceType,
        source_ref: sourceRef,
        conversion,
        montant: conversion === 'totale' ? null : amount,
        motif,
        client_nom: clientNom,
        client_tel: clientTel,
        client_id: clientId || undefined,
        magasin_id: vente?.magasin_id || undefined,
      });
      onCreated?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Création de l’avoir impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }}>
      <div className="modal-dark w-full max-w-md" data-testid="convertir-avoir-modal">
        <div className="modal-dark-header flex items-center justify-between">
          <h3 className="font-bold text-lg">Convertir en avoir</h3>
          <button type="button" onClick={onClose} className="rm-touch text-[var(--rm-gray-400)] hover:text-white">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[var(--rm-gray-400)] font-mono">{sourceRef}</p>
          {eligible && (
            <div className="rounded-xl border p-3 text-sm space-y-1" style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}>
              <div className="flex justify-between"><span className="text-[var(--rm-gray-500)]">Payé</span><span>{Number(eligible.montant_paye || 0).toFixed(2)} €</span></div>
              <div className="flex justify-between"><span className="text-[var(--rm-gray-500)]">Déjà converti</span><span>{Number(eligible.montant_deja_converti || 0).toFixed(2)} €</span></div>
              <div className="flex justify-between font-bold text-[var(--rm-success)]"><span>Éligible</span><span>{max.toFixed(2)} €</span></div>
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setConversion('totale'); setMontant(String(max)); }}
              className={`flex-1 py-3 min-h-[44px] rounded-xl text-sm font-semibold border ${conversion === 'totale' ? 'btn-accent' : 'hover:bg-white/5'}`}
              style={conversion === 'totale' ? undefined : { borderColor: 'var(--rm-border-subtle)' }}
            >
              Total
            </button>
            <button
              type="button"
              onClick={() => setConversion('partielle')}
              className={`flex-1 py-3 min-h-[44px] rounded-xl text-sm font-semibold border ${conversion === 'partielle' ? 'btn-accent' : 'hover:bg-white/5'}`}
              style={conversion === 'partielle' ? undefined : { borderColor: 'var(--rm-border-subtle)' }}
            >
              Partiel
            </button>
          </div>
          {conversion === 'partielle' && (
            <div>
              <label className="block text-sm text-[var(--rm-gray-300)] mb-2">Montant (€)</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                max={max}
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                className={inputClass}
                data-testid="avoir-montant-input"
              />
            </div>
          )}
          <div>
            <label className="block text-sm text-[var(--rm-gray-300)] mb-2">Motif (optionnel)</label>
            <input value={motif} onChange={(e) => setMotif(e.target.value)} className={inputClass} placeholder="Retour, geste commercial…" />
          </div>
          <div className="relative">
            <label className="block text-sm text-[var(--rm-gray-300)] mb-2">
              Client {clientLocked ? <span className="text-[10px] text-emerald-400">(dossier réparation)</span> : <span className="text-[10px] text-gray-500">(obligatoire)</span>}
            </label>
            <input
              value={clientNom}
              onChange={(e) => { setClientNom(e.target.value); setClientId(''); }}
              className={`${inputClass} ${clientLocked ? 'opacity-80' : ''}`}
              placeholder={clientLocked ? '' : 'Nom du client…'}
              readOnly={clientLocked}
              data-testid="avoir-client-nom"
            />
            {!clientLocked && suggestions.length > 0 && (
              <ul
                className="absolute z-20 left-0 right-0 mt-1 rounded-xl border max-h-48 overflow-y-auto shadow-2xl"
                style={{ background: 'var(--rm-bg-panel)', borderColor: 'var(--rm-border-subtle)' }}
                data-testid="avoir-client-suggestions"
              >
                {suggestions.map((c) => (
                  <li key={c.id || c.telephone}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-white/5"
                      onClick={() => {
                        setClientNom(c.nom || '');
                        setClientTel(c.telephone || '');
                        setClientId(c.id || '');
                        setSuggestions([]);
                      }}
                    >
                      <span className="font-semibold">{c.nom}</span>
                      {c.telephone ? <span className="text-gray-500 ml-2">{c.telephone}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="block text-sm text-[var(--rm-gray-300)] mb-2">Téléphone</label>
            <input
              value={clientTel}
              onChange={(e) => setClientTel(e.target.value)}
              className={`${inputClass} ${clientLocked ? 'opacity-80' : ''}`}
              placeholder="0694…"
              readOnly={clientLocked}
            />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 min-h-[44px] rounded-xl border font-bold text-[var(--rm-gray-300)]" style={{ borderColor: 'var(--rm-border-subtle)' }}>
              Annuler
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={loading || max <= 0}
              className="flex-1 py-3 min-h-[44px] rounded-xl btn-accent font-bold disabled:opacity-40"
              data-testid="avoir-confirm"
            >
              {loading ? 'Création…' : 'Créer l’avoir'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
